import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type ApiError, isAbortError } from "@/lib/api/errors";
import {
  listPolizzaRuns,
  listRuns,
  type PolizzaRunStatusListResponse,
  type RunStatusListResponse,
} from "@/lib/api/extract";
import { useLatest } from "@/lib/hooks/useLatest";
import {
  DEFAULT_RUN_HISTORY_QUERY,
  normalizeDate,
  RUN_HISTORY_PAGE_SIZE,
  type RunHistoryQuery,
  type UnifiedRunHistoryItem,
} from "./status-domain";

const RUN_HISTORY_FETCH_LIMIT = 1000;

type RefreshRunsOptions = {
  silent?: boolean;
  queryOverride?: RunHistoryQuery;
};

export type RefreshRunsResult = {
  ok: boolean;
  errorMessage: string | null;
};

type UseRunsHistoryOptions = {
  onUnauthorized: () => void;
  query: RunHistoryQuery;
};

export type UseRunsHistoryResult = {
  runs: UnifiedRunHistoryItem[];
  totalRuns: number;
  pageCount: number;
  isRefreshingRuns: boolean;
  clearRuns: () => void;
  refreshRuns: (options?: RefreshRunsOptions) => Promise<RefreshRunsResult>;
};

function toQueryKey(query: RunHistoryQuery): string {
  return JSON.stringify(query);
}

function sortMergedRuns(
  left: UnifiedRunHistoryItem,
  right: UnifiedRunHistoryItem,
): number {
  const leftDate =
    normalizeDate(left.startedAt) || normalizeDate(left.finishedAt);
  const rightDate =
    normalizeDate(right.startedAt) || normalizeDate(right.finishedAt);

  if (rightDate !== leftDate) {
    return rightDate - leftDate;
  }

  return right.id - left.id;
}

function matchesHistoryQuery(
  item: UnifiedRunHistoryItem,
  query: RunHistoryQuery,
): boolean {
  const normalizedFileQuery = query.fileQuery.trim().toLocaleLowerCase();

  if (query.kind !== "all" && item.kind !== query.kind) {
    return false;
  }

  if (query.status !== "all" && item.status !== query.status) {
    return false;
  }

  if (!normalizedFileQuery) {
    return true;
  }

  return (item.inputFilename ?? "")
    .toLocaleLowerCase()
    .includes(normalizedFileQuery);
}

function paginateRuns(
  items: UnifiedRunHistoryItem[],
  page: number,
): UnifiedRunHistoryItem[] {
  const startIndex = (page - 1) * RUN_HISTORY_PAGE_SIZE;
  return items.slice(startIndex, startIndex + RUN_HISTORY_PAGE_SIZE);
}

function mapFattureRuns(
  response: RunStatusListResponse,
): UnifiedRunHistoryItem[] {
  return (response.items ?? []).map((item) => ({
    kind: "fatture_ddt" as const,
    id: item.run_id,
    status: item.status,
    inputFilename: item.input_filename ?? null,
    renderDpiUsed: item.render_dpi_used ?? null,
    totalPages: item.total_pages ?? 0,
    processedPages: item.processed_pages ?? 0,
    failedPages: item.failed_pages ?? 0,
    fatturePages: item.fatture_pages,
    ddtPages: item.ddt_pages,
    unknownPages: item.unknown_pages,
    correctFattureCount: item.correct_fatture_count,
    mismatchesCount: item.mismatches_count,
    reconciliationStatus: item.reconciliation_status ?? null,
    progressPct: item.progress_pct ?? 0,
    startedAt: item.started_at ?? null,
    finishedAt: item.finished_at ?? null,
    lastError: item.last_error ?? null,
  }));
}

function mapPolizzaRuns(
  response: PolizzaRunStatusListResponse,
): UnifiedRunHistoryItem[] {
  return (response.items ?? []).map((item) => ({
    kind: "polizza_carico" as const,
    id: item.polizza_id,
    status: item.status,
    inputFilename: item.input_filename ?? null,
    renderDpiUsed: item.render_dpi_used ?? null,
    totalPages: item.total_pages ?? 0,
    processedPages: item.processed_pages ?? 0,
    failedPages: item.failed_pages ?? 0,
    progressPct: item.progress_pct ?? 0,
    startedAt: item.started_at ?? null,
    finishedAt: item.finished_at ?? null,
    lastError: item.last_error ?? null,
  }));
}

export function useRunsHistory({
  onUnauthorized,
  query,
}: UseRunsHistoryOptions): UseRunsHistoryResult {
  const [runs, setRuns] = useState<UnifiedRunHistoryItem[]>([]);
  const [totalRuns, setTotalRuns] = useState(0);
  const [isRefreshingRuns, setIsRefreshingRuns] = useState(false);
  const onUnauthorizedRef = useLatest(onUnauthorized);
  const queryRef = useLatest(query);
  const refreshRunsInFlightRef = useRef<Promise<RefreshRunsResult> | null>(
    null,
  );
  const refreshRunsKeyRef = useRef<string | null>(null);
  const refreshRunsAbortRef = useRef<AbortController | null>(null);

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(totalRuns / RUN_HISTORY_PAGE_SIZE)),
    [totalRuns],
  );

  const clearRuns = useCallback(() => {
    refreshRunsAbortRef.current?.abort();
    refreshRunsAbortRef.current = null;
    refreshRunsInFlightRef.current = null;
    refreshRunsKeyRef.current = null;
    setRuns([]);
    setTotalRuns(0);
    setIsRefreshingRuns(false);
  }, []);

  const refreshRuns = useCallback(
    async (options: RefreshRunsOptions = {}): Promise<RefreshRunsResult> => {
      const silent = options.silent ?? false;
      const nextQuery = options.queryOverride ?? queryRef.current;
      const queryKey = toQueryKey(nextQuery);

      if (
        refreshRunsInFlightRef.current &&
        refreshRunsKeyRef.current === queryKey
      ) {
        return refreshRunsInFlightRef.current;
      }

      const abortController = new AbortController();
      refreshRunsAbortRef.current?.abort();
      refreshRunsAbortRef.current = abortController;
      refreshRunsKeyRef.current = queryKey;

      const request = (async (): Promise<RefreshRunsResult> => {
        try {
          if (!silent) {
            setIsRefreshingRuns(true);
          }

          const trimmedFileQuery = nextQuery.fileQuery.trim() || null;
          const shouldFilterClientSide =
            nextQuery.kind !== "all" ||
            nextQuery.status !== "all" ||
            trimmedFileQuery !== null;
          const statusFilter =
            shouldFilterClientSide || nextQuery.status === "all"
              ? null
              : nextQuery.status;
          const requestLimit = shouldFilterClientSide
            ? RUN_HISTORY_FETCH_LIMIT
            : RUN_HISTORY_PAGE_SIZE;
          const requestOffset = shouldFilterClientSide
            ? 0
            : (nextQuery.page - 1) * RUN_HISTORY_PAGE_SIZE;

          if (nextQuery.kind === "fatture_ddt") {
            const response = await listRuns({
              limit: requestLimit,
              offset: requestOffset,
              status: statusFilter,
              inputFilenameContains: trimmedFileQuery,
              signal: abortController.signal,
            });

            const filteredRuns = mapFattureRuns(response).filter((item) =>
              matchesHistoryQuery(item, nextQuery),
            );

            setRuns(paginateRuns(filteredRuns, nextQuery.page));
            setTotalRuns(filteredRuns.length);
            return { ok: true, errorMessage: null };
          }

          if (nextQuery.kind === "polizza_carico") {
            const response = await listPolizzaRuns({
              limit: requestLimit,
              offset: requestOffset,
              status: statusFilter,
              inputFilenameContains: trimmedFileQuery,
              signal: abortController.signal,
            });

            const filteredRuns = mapPolizzaRuns(response).filter((item) =>
              matchesHistoryQuery(item, nextQuery),
            );

            setRuns(paginateRuns(filteredRuns, nextQuery.page));
            setTotalRuns(filteredRuns.length);
            return { ok: true, errorMessage: null };
          }

          const [runsResponse, polizzeResponse] = await Promise.all([
            listRuns({
              limit: requestLimit,
              offset: 0,
              status: statusFilter,
              inputFilenameContains: trimmedFileQuery,
              signal: abortController.signal,
            }),
            listPolizzaRuns({
              limit: requestLimit,
              offset: 0,
              status: statusFilter,
              inputFilenameContains: trimmedFileQuery,
              signal: abortController.signal,
            }),
          ]);

          const merged = [
            ...mapFattureRuns(runsResponse),
            ...mapPolizzaRuns(polizzeResponse),
          ]
            .filter((item) => matchesHistoryQuery(item, nextQuery))
            .sort(sortMergedRuns);

          setRuns(paginateRuns(merged, nextQuery.page));
          setTotalRuns(merged.length);
          return { ok: true, errorMessage: null };
        } catch (error) {
          if (isAbortError(error)) {
            return { ok: false, errorMessage: null };
          }

          const apiError = error as ApiError;
          if (apiError.status === 401) {
            onUnauthorizedRef.current();
            return { ok: false, errorMessage: null };
          }

          return {
            ok: false,
            errorMessage:
              apiError.message ??
              "Impossibile caricare lo storico delle analisi.",
          };
        } finally {
          if (refreshRunsAbortRef.current === abortController) {
            refreshRunsAbortRef.current = null;
          }
          if (!silent) {
            setIsRefreshingRuns(false);
          }
        }
      })();

      refreshRunsInFlightRef.current = request;

      try {
        return await request;
      } finally {
        if (refreshRunsInFlightRef.current === request) {
          refreshRunsInFlightRef.current = null;
        }
        if (refreshRunsKeyRef.current === queryKey) {
          refreshRunsKeyRef.current = null;
        }
      }
    },
    [onUnauthorizedRef, queryRef],
  );

  useEffect(() => {
    const nextQuery = {
      ...DEFAULT_RUN_HISTORY_QUERY,
      ...query,
      page: Math.max(1, query.page),
    };

    void refreshRuns({ queryOverride: nextQuery });
  }, [query, refreshRuns]);

  return {
    runs,
    totalRuns,
    pageCount,
    isRefreshingRuns,
    clearRuns,
    refreshRuns,
  };
}
